import {
  INTEREST_TYPE_TIL,
  QUERY_MODEL_CATEGORY,
} from '@/app/constants/calculator';
import { getLcvColorId } from '@/app/lcv/components/shared/color-list/utils';
import { type Month } from '@/app/lcv/types/calculator';
import type { LCVTilInterest } from '@/app/types/models/lcv';
import {
  buildCategoryModelKey,
  getLcvModelColors,
  getLcvModelDefaultColor,
} from '@/services/lineup-model/lcv-product-compare/lcv-models-helpers';
import type {
  CategoryDefaultGrades,
  LcvModelWithFieldKey,
} from '@/services/lineup-model/lcv-product-compare/types';
import type {
  LcvModelCategory,
  LcvModelColor,
  LcvModelSubCategory,
} from '@/types/tis-cms/types';

import type {
  BuildCalculatorRegisterRemarkInput,
  CalculatorMonthlyInstallmentInput,
  ModelForDefaultFinancing,
  RawTilInterestResponse,
} from './types';
import {
  getCategoryKey,
  getModelSubCategoryListFromCategory,
  parseLCVTilInterest,
  resolveDefaultGradeUid,
} from './utility';

export { buildCategoryModelKey, getLcvModelColors, getLcvModelDefaultColor };

export const buildParsedTilVehicleModel = (res: RawTilInterestResponse) => {
  return {
    suv: res.suv.map((o) => parseLCVTilInterest(o)),
    pickup: res.pickup.map((o) => parseLCVTilInterest(o)),
    pickup_4_door_hi_lander: res.pickup_4_door_hi_lander.map((o) =>
      parseLCVTilInterest(o)
    ),
    pickup_4_door_cab4: res.pickup_4_door_cab4.map((o) =>
      parseLCVTilInterest(o)
    ),
    pickup_2_door_hi_lander: res.pickup_2_door_hi_lander.map((o) =>
      parseLCVTilInterest(o)
    ),
    pickup_2_door_space_cab: res.pickup_2_door_space_cab.map((o) =>
      parseLCVTilInterest(o)
    ),
    x_series: res.x_series.map((o) => parseLCVTilInterest(o)),
    v_cross: res.v_cross.map((o) => parseLCVTilInterest(o)),
    spark: res.spark.map((o) => parseLCVTilInterest(o)),
    mu_x: res.mu_x.map((o) => parseLCVTilInterest(o)),
  };
};

export const getDefaultFinancingFromSelectedModel = (
  defaultSelectedModel: ModelForDefaultFinancing,
  defaultPercent: number
) => {
  const priceString = (
    (defaultSelectedModel?.price ?? 0) +
    (defaultSelectedModel?.defaultColor?.price ?? 0)
  ).toString();
  const defaultPercentStr = defaultPercent.toString();
  const defaultSelectedModelPayment = `${
    (parseFloat(defaultPercentStr) * parseInt(priceString)) / 100
  }`;
  return {
    defaultPercent: defaultPercentStr,
    defaultSelectedModelPayment,
    defaultSelectedModelPrice: priceString,
  };
};

export const isQueryModelEqualToDefaultClass = <T extends string>(
  model: T,
  defaultSelectClass: string,
  queryClassByModel: Record<T, string>
): boolean => {
  return defaultSelectClass === queryClassByModel[model];
};

export const filterModelCategoriesByUid = (
  categories: LcvModelCategory[],
  classKey: string
) =>
  categories.filter(
    (category) =>
      category.documentId === classKey || getCategoryKey(category) === classKey
  );

export const pickModelCategoryPreferNonEuro4 = (
  categories: LcvModelCategory[]
): LcvModelCategory | null => {
  if (categories.length === 0) return null;
  return (
    categories.find((o) => !o.nameTh?.includes('(EURO 4)')) ??
    categories[0] ??
    null
  );
};

export const resolveMonthFromQueryTerm = (
  term: string,
  monthOptions: { value: string; label: string }[],
  defaultOption: Month
): Month => {
  const hasTermInOption = monthOptions.find((option) => option.value === term);
  if (hasTermInOption) {
    return { label: term, value: term };
  }
  return defaultOption;
};

export const isCalculatorQueryClassParam = (value: string): boolean =>
  Object.prototype.hasOwnProperty.call(QUERY_MODEL_CATEGORY, value);

export type ResolvedModelFromQueryParam = {
  model: LcvModelWithFieldKey;
  color: LcvModelColor;
};

type AllModelsMap = { [key: string]: LcvModelWithFieldKey[] };

const modelQueryKeys = (m: LcvModelWithFieldKey): string[] =>
  [m.fieldKey, m.documentId].filter(Boolean) as string[];

/** Text after the last `_` (paint id in URLs), or null if there is no suffix. */
const colorSuffixFromParam = (param: string): string | null => {
  const i = param.lastIndexOf('_');
  if (i <= 0 || i >= param.length - 1) return null;
  return param.slice(i + 1);
};

const colorForModel = (
  model: LcvModelWithFieldKey,
  colorId: string | null
): LcvModelColor => {
  const defaultColor = getLcvModelDefaultColor(model);
  if (!defaultColor) {
    throw new Error(`Model ${model.documentId} has no default color`);
  }
  if (!colorId) return defaultColor;
  return (
    getLcvModelColors(model).find((c) => getLcvColorId(c) === colorId) ??
    defaultColor
  );
};

const buildKeyIndex = (allModels: AllModelsMap) => {
  const rows: { key: string; model: LcvModelWithFieldKey }[] = [];
  for (const model of Object.values(allModels).flat()) {
    for (const key of modelQueryKeys(model)) {
      rows.push({ key, model });
    }
  }
  rows.sort((a, b) => b.key.length - a.key.length);
  const norm = (v: string) => v.toLowerCase().replace(/[^a-z0-9]/g, '');
  return {
    rows,
    findByKey: (k: string) =>
      rows.find(({ key }) => key === k) ??
      rows.find(({ key }) => norm(key) === norm(k)),
  };
};

export const findModelAndColorFromModelQueryParam = (
  param: string,
  allModels: AllModelsMap
): ResolvedModelFromQueryParam | null => {
  const { rows, findByKey } = buildKeyIndex(allModels);

  const firstUnderscore = param.indexOf('_');
  if (firstUnderscore > 0) {
    const compareModelId = param.slice(0, firstUnderscore);
    const compareColorId = param.slice(firstUnderscore + 1);
    const compareHit = findByKey(compareModelId);

    if (compareHit) {
      return {
        model: compareHit.model,
        color: colorForModel(
          compareHit.model,
          !!compareHit ? compareColorId : null
        ),
      };
    }
  }

  const colorId = colorSuffixFromParam(param);
  const modelKey = colorId ? param.slice(0, param.lastIndexOf('_')) : null;

  if (modelKey) {
    const hit = findByKey(modelKey);
    if (hit) {
      return { model: hit.model, color: colorForModel(hit.model, colorId) };
    }
  }

  for (const { key, model } of rows) {
    if (param === key) {
      return { model, color: colorForModel(model, null) };
    }
    if (param.startsWith(`${key}_`)) {
      const rest = param.slice(key.length + 1);
      if (rest) {
        return { model, color: colorForModel(model, rest) };
      }
    }
  }
  return null;
};

/** Longest first so e.g. `x-series` wins over shorter tokens. */
const QUERY_CLASS_SLUGS = (
  Object.entries(QUERY_MODEL_CATEGORY) as [string, string][]
)
  .map(([slug, classUid]) => ({ slug, classUid }))
  .sort((a, b) => b.slug.length - a.slug.length);

/**
 * Deep links like `mu-x-2-0-rs-4wd_bavarian-black-mica` embed a grade + paint in the
 * path; when no concrete grade resolves, open the line with the **default** grade and
 * color. Detect `line-slug-` (hyphen after line), not `line-slug_`.
 */
const getLineGradeMarketingParam = (
  param: string
): { slug: string; classUid: string; remainder: string } | null => {
  for (const { slug, classUid } of QUERY_CLASS_SLUGS) {
    if (param.startsWith(`${slug}-`)) {
      return {
        slug,
        classUid,
        remainder: param.slice(slug.length + 1),
      };
    }
  }
  return null;
};

const matchClassPrefixAndRemainder = (
  param: string,
  modelCategories: LcvModelCategory[]
): { classUid: string; matchedPrefix: string; remainder: string } | null => {
  const uids = [
    ...new Set([
      ...Object.values(QUERY_MODEL_CATEGORY),
      ...modelCategories.map((c) => c.documentId),
    ]),
  ].sort((a, b) => b.length - a.length);

  for (const uid of uids) {
    if (param === uid) {
      return { classUid: uid, matchedPrefix: uid, remainder: '' };
    }
    if (param.startsWith(`${uid}_`)) {
      return {
        classUid: uid,
        matchedPrefix: uid,
        remainder: param.slice(uid.length + 1),
      };
    }
  }

  for (const { slug, classUid } of QUERY_CLASS_SLUGS) {
    if (param === slug) {
      return { classUid, matchedPrefix: slug, remainder: '' };
    }
    if (param.startsWith(`${slug}_`)) {
      return {
        classUid,
        matchedPrefix: slug,
        remainder: param.slice(slug.length + 1),
      };
    }
    if (param.startsWith(`${slug}-`)) {
      return {
        classUid,
        matchedPrefix: slug,
        remainder: param.slice(slug.length + 1),
      };
    }
  }
  return null;
};

const defaultGradeInCategory = (
  cat: LcvModelCategory,
  allModels: AllModelsMap,
  modelYear: string,
  defaultGradesByCategoryKey?: Record<string, CategoryDefaultGrades>
): ResolvedModelFromQueryParam | null => {
  const categoryKey = buildCategoryModelKey(cat.documentId, modelYear);
  const list = allModels[categoryKey] || [];
  if (list.length === 0) return null;

  const subList = getModelSubCategoryListFromCategory(
    getCategoryKey(cat),
    list
  );
  const defaultUid = resolveDefaultGradeUid({
    category: cat,
    subCategory: subList?.[0] ?? null,
    models: list,
    defaultGrades: defaultGradesByCategoryKey?.[categoryKey],
    modelYear,
  });
  const model = list.find((m) => m.documentId === defaultUid) ?? list[0];
  return { model, color: colorForModel(model, null) };
};

const tryResolveByClassUid = (
  classUid: string,
  modelCategories: LcvModelCategory[],
  allModels: AllModelsMap,
  modelYear: string,
  defaultGradesByCategoryKey?: Record<string, CategoryDefaultGrades>
): {
  resolved: ResolvedModelFromQueryParam;
  category: LcvModelCategory;
} | null => {
  const cat = pickModelCategoryPreferNonEuro4(
    filterModelCategoriesByUid(modelCategories, classUid)
  );
  if (!cat) return null;
  const resolved = defaultGradeInCategory(
    cat,
    allModels,
    modelYear,
    defaultGradesByCategoryKey
  );
  return resolved ? { resolved, category: cat } : null;
};

export const resolveCalculatorModelQueryWithFallbacks = (
  param: string,
  allModels: AllModelsMap,
  modelCategories: LcvModelCategory[],
  defaultModelCategory: LcvModelCategory,
  modelYear: string,
  defaultGradesByCategoryKey?: Record<string, CategoryDefaultGrades>
): {
  resolved: ResolvedModelFromQueryParam;
  category: LcvModelCategory;
} | null => {
  const categoryForClassUid = (classUid: string): LcvModelCategory =>
    pickModelCategoryPreferNonEuro4(
      filterModelCategoriesByUid(modelCategories, classUid)
    ) ?? defaultModelCategory;

  const direct = findModelAndColorFromModelQueryParam(param, allModels);
  if (direct) {
    return {
      resolved: direct,
      category:
        getModelCategoryForLcvModel(
          direct.model,
          modelCategories,
          allModels,
          modelYear
        ) ?? defaultModelCategory,
    };
  }

  const lineMarketing = getLineGradeMarketingParam(param);
  if (lineMarketing) {
    const cat = categoryForClassUid(lineMarketing.classUid);
    const subsetKey = buildCategoryModelKey(cat.documentId, modelYear);
    const subsetList = allModels[subsetKey] || [];
    const subsetMap: AllModelsMap = { [subsetKey]: subsetList };

    const scopedFull = findModelAndColorFromModelQueryParam(param, subsetMap);
    if (scopedFull) {
      return { resolved: scopedFull, category: cat };
    }

    if (lineMarketing.remainder) {
      const scopedRemainder = findModelAndColorFromModelQueryParam(
        lineMarketing.remainder,
        subsetMap
      );
      if (scopedRemainder) {
        return { resolved: scopedRemainder, category: cat };
      }
    }

    const byLine = tryResolveByClassUid(
      lineMarketing.classUid,
      modelCategories,
      allModels,
      modelYear,
      defaultGradesByCategoryKey
    );
    if (byLine) {
      return byLine;
    }
  }

  const prefixMatch = matchClassPrefixAndRemainder(param, modelCategories);
  if (prefixMatch) {
    const cat = categoryForClassUid(prefixMatch.classUid);
    const subsetKey = buildCategoryModelKey(cat.documentId, modelYear);
    const subsetList = allModels[subsetKey] || [];
    const subsetMap: AllModelsMap = { [subsetKey]: subsetList };

    if (prefixMatch.remainder) {
      const scoped = findModelAndColorFromModelQueryParam(
        prefixMatch.remainder,
        subsetMap
      );
      if (scoped) {
        return { resolved: scoped, category: cat };
      }

      const scopedFull = findModelAndColorFromModelQueryParam(param, subsetMap);
      if (scopedFull) {
        return { resolved: scopedFull, category: cat };
      }
    }

    const resolved = defaultGradeInCategory(
      cat,
      allModels,
      modelYear,
      defaultGradesByCategoryKey
    );
    if (resolved) {
      return { resolved, category: cat };
    }
  }

  const fallback = defaultGradeInCategory(
    defaultModelCategory,
    allModels,
    modelYear,
    defaultGradesByCategoryKey
  );
  return fallback
    ? { resolved: fallback, category: defaultModelCategory }
    : null;
};

export const getModelCategoryForLcvModel = (
  model: LcvModelWithFieldKey,
  modelCategories: LcvModelCategory[],
  allModels: AllModelsMap,
  modelYear: string
): LcvModelCategory | null => {
  for (const cat of modelCategories) {
    const list = allModels[buildCategoryModelKey(cat.documentId, modelYear)];
    if (list?.some((m) => m.documentId === model.documentId)) {
      return cat;
    }
  }
  return null;
};

export const getSubCategoryForLcvModel = (
  model: LcvModelWithFieldKey,
  subCategoryList: LcvModelSubCategory[] | null
): LcvModelSubCategory | null => {
  if (!subCategoryList?.length) return null;
  const subDocumentId = model.subCategory?.documentId;
  if (!subDocumentId) return subCategoryList[0];
  return (
    subCategoryList.find((s) => s.documentId === subDocumentId) ??
    subCategoryList[0]
  );
};

export const computePaymentStringFromPercent = (
  percent: string,
  price: string
) => {
  const p = parseFloat(price);
  if (Number.isNaN(p) || p <= 0) return '0';
  return `${Math.round((parseFloat(percent) * p) / 100)}`;
};

export const computePercentStringFromPayment = (
  payment: string,
  price: string
) => {
  return ((parseFloat(payment) * 100) / parseFloat(price)).toFixed(2);
};

export const getCalculatorMonthlyInstallment = (
  input: CalculatorMonthlyInstallmentInput
) => {
  const paymentWithoutDown =
    parseInt(input.price, 10) - parseInt(input.payment, 10);
  const mrate =
    input.isTilLeasing && input.tilInterestList?.[0]
      ? input.tilInterestList[0][`m${input.month}` as keyof LCVTilInterest]
      : input.interest;

  const paymentWithInterest =
    (paymentWithoutDown +
      (paymentWithoutDown *
        parseFloat(mrate as string) *
        Math.floor(parseInt(input.month, 10) / 12)) /
        100) /
    parseInt(input.month, 10);

  return Math.ceil(paymentWithInterest);
};

export const buildCalculatorRegisterRemark = (
  input: BuildCalculatorRegisterRemarkInput
) => {
  return `${input.modelCategoryName}|${input.modelName}|สีรถ ${
    input.colorName
  }|เงินดาวน์ ${(
    (parseInt(input.payment, 10) / parseInt(input.price, 10)) *
    100
  ).toFixed(2)}% ${parseInt(
    input.payment,
    10
  ).toLocaleString()} บาท|${input.monthValue} เดือน|อัตราดอกเบี้ย ${
    input.isTilLeasing ? INTEREST_TYPE_TIL : `กรอกเอง ${input.interest} %`
  }`;
};
